import React, { useCallback, useEffect, useMemo, useRef, type Ref } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bot, User, Loader2, AlertCircle, RotateCcw, Clock, Wrench, TriangleAlert, ChevronsRight, MessageSquareText, ChevronsLeft, BotMessageSquare, CirclePlay, ExternalLink } from 'lucide-react'
import { chatsApi } from '@/api/chats'
import { useAuth } from '@/hooks/useAuth'
import { useTenantKey } from '@/hooks/useTenantKey'
import { useAgentLabels, useChatPendingChanges, useWorkflowRunChangeSets, useSecurityReviewRun, type ChatAgentSelectionState } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { SecurityReviewFindingsSection } from '@/components/projects/workflows/runs/SecurityReviewFindingsSection'
import { MarkdownViewer } from '@/components/MarkdownViewer'
import { SessionLogViewer } from '@/components/SessionLogViewer'
import { parseSessionLogs } from '@/utils/parseSessionLogs'
import { formatDateTimeLong } from '@/lib/timeFormatters'
import { ChatAttachmentChip } from './ChatAttachmentChip'
import { ChatInlineImage } from './ChatInlineImage'
import { isRenderableImage } from '@/lib/attachments'
import { ChatComposer } from './ChatComposer'
import type { ChatInputHandle } from './ChatInput'
import { ChatPendingChangesCard } from './ChatPendingChangesCard'
import { ContextReposBar } from './ContextReposBar'
import { NodeThinkingSpinner } from './NodeThinkingSpinner'
import { ResponseWaitNotice } from './ResponseWaitNotice'
import { useChatRunStream } from '@/hooks/useChatRunStream'
import type { ChatSendError } from '@/hooks/useChatStream'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import {
  ChatCommandForm,
  WorkflowTriggerForm,
  ChatCommandResultCard,
  WorkflowRunCard
} from '@/components/projects/workflows/chat'
import { WorkflowStatusBadge } from '@/components/projects/workflows/shared'
import { ConnectorUnavailableBanner } from './ConnectorUnavailableBanner'
import { ContextBudgetExhaustedBanner } from './ContextBudgetExhaustedBanner'
import { ChatSendErrorBanner } from './ChatSendErrorBanner'
import { AnsweredQuestionBubble } from './AnsweredQuestionBubble'
import { NodeQuestionPaginator } from './NodeQuestionPaginator'
import { ModelChangeDivider } from './ModelChangeDivider'
import { CHAT_AGENT_PROVIDERS } from '@/types'
import type { ChatMessage, ChatCommandDescriptor, WorkflowDefinitionSummary, WorkflowTriggerSchema, WorkflowRunStatus, ConnectorType, Skill } from '@/types'
import type { ConnectorExpiredNotificationPayload, ConnectorMissingNotificationPayload, ContextBudgetExhaustedNotificationPayload, ChatAttachment, MessageAttachment } from '@/types/chat'
import type { ContextSourceListItem } from '@/types/contextSource'
import { ConnectorExpiredNotificationType, ConnectorMissingNotificationType, ContextBudgetExhaustedNotificationType } from '@/lib/workflowErrorCodes'

interface ChatAreaProps {
  chatId: string | null
  chatTitle: string | null
  projectId: string
  messages: ChatMessage[]
  isLoading: boolean
  isStreaming: boolean
  isCancelling: boolean
  streamingLogs: string
  containerStatus: string | null
  usesContainer: boolean
  streamStartedAt: string | null
  streamEffort: string | null
  pendingUserMessage: string | null
  sendError: ChatSendError | null
  onDismissSendError: () => void
  composerRef?: Ref<ChatInputHandle>
  isContainerMode: boolean
  containerLockedByAttachments: boolean
  onContainerModeChange: (value: boolean) => void
  onSendMessage: (message: string, useContainer: boolean) => boolean
  onConnectorReconnected?: (connectorType: ConnectorType) => void
  onCancelStream: () => void
  contextRepos: ContextSourceListItem[]
  commands: ChatCommandDescriptor[]
  workflows: WorkflowDefinitionSummary[]
  skills: Skill[]
  skillsLoading?: boolean
  selectedSkills: Skill[]
  onSelectSkill: (skill: Skill) => void
  onRemoveSkill: (skillId: string) => void
  onClearSkills: () => void
  activeCommand: ChatCommandDescriptor | null
  commandPrefill: Record<string, string> | null
  activeWorkflow: WorkflowDefinitionSummary | null
  triggerSchema: WorkflowTriggerSchema | null
  schemaLoading: boolean
  schemaErrorMessage: string | null
  onCommandSelect: (command: ChatCommandDescriptor) => void
  onCommandCancel: () => void
  onCommandExecute: (params: Record<string, string>) => void
  isExecutingCommand: boolean
  onCommandRunAgain: (commandName: string, params: Record<string, string>) => void
  onWorkflowSelect: (workflow: WorkflowDefinitionSummary) => void
  onWorkflowCancel: () => void
  onWorkflowTrigger: (inputValuesJson: string, variableGroupIds: string[]) => void
  onWorkflowRetrySchema: () => void
  isTriggering: boolean
  attachments: ChatAttachment[]
  attachmentPreviews: Record<string, string>
  pendingUploadCount: number
  onAttachFiles: (files: File[], withPreviews?: boolean) => void
  onCancelUpload: () => void
  onRemoveAttachment: (id: string) => void
  pendingUserMessageAttachments?: MessageAttachment[]
  messageAttachmentsCache?: Map<string, MessageAttachment[]>
  onAttachmentLoadError?: () => void
  agent: ChatAgentSelectionState
  isSidebarOpen: boolean
  setSidebarOpen: (value: boolean) => void
  runStream: ReturnType<typeof useChatRunStream>
}

export function ChatArea({
  chatId,
  chatTitle,
  projectId,
  messages,
  isLoading,
  isStreaming,
  isCancelling,
  streamingLogs,
  containerStatus,
  usesContainer,
  streamStartedAt,
  streamEffort,
  pendingUserMessage,
  sendError,
  onDismissSendError,
  composerRef,
  isContainerMode,
  containerLockedByAttachments,
  onContainerModeChange,
  onSendMessage,
  onConnectorReconnected,
  onCancelStream,
  contextRepos,
  commands,
  workflows,
  skills,
  skillsLoading,
  selectedSkills,
  onSelectSkill,
  onRemoveSkill,
  onClearSkills,
  activeCommand,
  commandPrefill,
  activeWorkflow,
  triggerSchema,
  schemaLoading,
  schemaErrorMessage,
  onCommandSelect,
  onCommandCancel,
  onCommandExecute,
  isExecutingCommand,
  onCommandRunAgain,
  onWorkflowSelect,
  onWorkflowCancel,
  onWorkflowTrigger,
  onWorkflowRetrySchema,
  isTriggering,
  attachments,
  attachmentPreviews,
  pendingUploadCount,
  onAttachFiles,
  onCancelUpload,
  onRemoveAttachment,
  pendingUserMessageAttachments,
  messageAttachmentsCache,
  onAttachmentLoadError,
  agent,
  isSidebarOpen,
  setSidebarOpen,
  runStream
}: Readonly<ChatAreaProps>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const hasConnectedRepositories = contextRepos.length > 0
  const tenantKey = useTenantKey()
  const { hasPermission } = useAuth(projectId)

  const { data: chatRuns } = useQuery({
    queryKey: tenantKey('projects', projectId, 'chats', chatId, 'runs'),
    queryFn: () => chatsApi.getRuns(projectId, chatId!),
    enabled: !!chatId,
    refetchInterval: 5_000,
  })
  const awaitingRun = chatRuns?.find((r) => r.status === 'AwaitingApproval') ?? null
  const currentRun = chatRuns?.find((r) => !r.isTerminal) ?? chatRuns?.[0] ?? null
  const { data: gateChangeSets } = useWorkflowRunChangeSets(projectId, awaitingRun?.runId ?? null)
  const { data: pendingChangeSet } = useChatPendingChanges(projectId, chatId)
  const { data: securityReviewRun } = useSecurityReviewRun(projectId, awaitingRun?.runId ?? null)
  const isSecurityReviewGate = securityReviewRun?.status === 'AwaitingReview'
  const canDecideSecurityReview = hasPermission(Permissions.Project.Workflow.Approve, projectId)
  const pendingGate = awaitingRun
    ? {
        runId: awaitingRun.runId,
        feedbackAvailable:
          (gateChangeSets?.gateFeedbackAvailable ?? false)
          || (gateChangeSets?.changeSets.some((c) => c.status === 'Pending' && c.feedbackAvailable) ?? false),
      }
    : null

  const turns = useMemo(() => groupIntoTurns(messages), [messages])
  const { modelLabel } = useAgentLabels()
  const modelChangeBefore = useMemo(() => {
    const resolveLabel = (raw: string): string => {
      const label = modelLabel(raw)
      if (label !== raw) return label
      return raw.startsWith('claude-') ? modelLabel(raw.slice(7)) : raw
    }
    const result = new Map<string, string>()
    let prevRaw: string | null = null
    for (const turn of turns) {
      if (turn.type === 'assistant' && turn.message.modelUsed) {
        const raw = turn.message.modelUsed
        if (prevRaw && prevRaw !== raw) result.set(turn.message.id, resolveLabel(raw))
        prevRaw = raw
      }
    }
    return result
  }, [turns, modelLabel])

  useEffect(() => {
      const container = scrollContainerRef.current
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
      }
  }, [messages, streamingLogs, pendingUserMessage, isStreaming, containerStatus, sendError, runStream.pendingUserInputRequest])

  if (!chatId) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
        <Bot className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-lg font-medium">AI Chat</p>
        <p className="text-sm">Select a chat or create a new one to get started.</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" aria-label="Loading messages" data-testid="chat-messages-loading" className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading messages...
      </div>
    )
  }

  const lastUserMessageText = [...messages].reverse().find(m => m.role === 'User')?.textContent
  const pendingAlreadyInMessages = !!pendingUserMessage && lastUserMessageText === pendingUserMessage
  const hasContent = turns.length > 0 || isStreaming || sendError || activeCommand || activeWorkflow
  const effortApplied = usesContainer || agent.selection?.agentProvider !== CHAT_AGENT_PROVIDERS.claude
  const streamStatusLabel = resolveStreamStatusLabel(containerStatus, !!streamingLogs, usesContainer && hasConnectedRepositories)
  const expiredRepoNames = new Set(
    turns.flatMap((t) => (t.type === 'connector-expired-notification' && t.payload.repositoryFullName ? [t.payload.repositoryFullName] : [])),
  )

  return (
    <div className="flex h-full min-h-0 gap-3">
      <div className="flex flex-[3] min-w-0 min-h-0 flex-col border rounded-xl bg-panel">
      {chatTitle && (
        <div className="border-b flex items-center px-4 py-3 justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="truncate text-sm font-semibold">{chatTitle}</h2>
            {currentRun && <WorkflowStatusBadge status={currentRun.status as WorkflowRunStatus} className="text-[10px] py-1 px-2" />}
            {pendingChangeSet?.status === 'Pending' && (
              <WorkflowStatusBadge status="AwaitingApproval" className="text-[10px] py-1 px-2" />
            )}
          </div>
          {hasContent && (chatRuns?.length ?? 0) > 0 && (
            <button
              type="button"
              className="flex items-center gap-2 px-2 py-1 text-sm font-bold text-foreground hover:bg-muted rounded-md"
              aria-label={isSidebarOpen ? 'Hide run details' : 'View run details'}
              onClick={() => setSidebarOpen(!isSidebarOpen)}
            >
              {isSidebarOpen ? <ChevronsLeft className="h-5 w-5 shrink-0" /> : <ChevronsRight className="h-5 w-5 shrink-0" />}
            </button>
        )}
        </div>
      )}
      <ContextReposBar repos={contextRepos} expiredRepoNames={expiredRepoNames} />
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto">
        <div className={`mx-auto px-4${hasContent ? '' : ' flex h-full flex-col justify-center'}`}>
          {!hasContent && (
            <div className="flex flex-col items-center justify-center text-muted-foreground">
              <MessageSquareText className="h-6 w-7 mb-3 opacity-30" />
              <p className="text-sm">
                Start sending your message or type{' '}
                <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">/</kbd>
                {' '}to run a command.
              </p>
            </div>
          )}

          {turns.map((turn) => {
            if (turn.type === 'user') {
              return (
                <UserBubble
                  key={turn.message.id}
                  text={turn.message.textContent || ''}
                  timestamp={turn.message.createdAt}
                  attachments={messageAttachmentsCache?.get(turn.message.id)}
                  onAttachmentLoadError={onAttachmentLoadError}
                />
              )
            }
            if (turn.type === 'answered-question') {
              return (
                <AnsweredQuestionBubble
                  key={turn.message.id}
                  payload={turn.payload}
                  timestamp={turn.message.createdAt}
                />
              )
            }
            if (turn.type === 'assistant') {
              return (
                <React.Fragment key={turn.message.id}>
                  {modelChangeBefore.has(turn.message.id) && (
                    <ModelChangeDivider modelLabel={modelChangeBefore.get(turn.message.id)!} />
                  )}
                  <AssistantBubble content={turn.message.textContent || ''} timestamp={turn.message.createdAt} modelUsed={turn.message.modelUsed} durationSeconds={turn.message.durationSeconds} toolCallsMade={turn.message.toolCallsMade} toolCallsTracked={turn.message.toolCallsTracked} />
                </React.Fragment>
              )
            }
            if (turn.type === 'system') {
              return <SystemBubble key={turn.message.id} message={turn.message} />
            }
            if (turn.type === 'connector-expired-notification') {
              return (
                <ConnectorUnavailableBanner
                  key={turn.message.id}
                  projectId={projectId}
                  connectorType={turn.payload.connectorType}
                  message={turn.payload.message}
                  onReconnected={onConnectorReconnected}
                />
              )
            }
            if (turn.type === 'connector-missing-notification') {
              return (
                <ConnectorUnavailableBanner
                  key={turn.message.id}
                  projectId={projectId}
                  connectorType={turn.payload.connectorType}
                  message={turn.payload.message}
                  variant="missing"
                />
              )
            }
            if (turn.type === 'context-budget-exhausted-notification') {
              return (
                <ContextBudgetExhaustedBanner
                  key={turn.message.id}
                  message={turn.payload.message}
                  currentSpendUsd={turn.payload.currentSpendUsd}
                  capUsd={turn.payload.capUsd}
                />
              )
            }
            if (turn.type === 'command-result') {
              return (
                <div key={turn.message.id} className="py-4 max-w-lg mx-auto">
                  <ChatCommandResultCard
                    commandName={turn.commandDisplayName}
                    result={turn.result}
                    onRunAgain={() => onCommandRunAgain(turn.commandName, turn.params)}
                  />
                </div>
              )
            }

            return (
              <div key={turn.message.id} className="py-4 max-w-lg mx-auto">
                <WorkflowRunCard runId={turn.runId} projectId={projectId} />
              </div>
            )
          })}

          <ChatPendingChangesCard key={chatId} projectId={projectId} chatId={chatId} />

          {pendingGate && (
            <div className="py-3">
              <div className="flex items-start gap-2 rounded-lg border px-4 py-3 text-xs bg-warning text-warning-foreground border-warning-foreground">
                <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {isSecurityReviewGate ? (
                  <div>
                    <b>Security review findings ready</b>
                    <p className="text-foreground">
                      Vote on each finding below, then approve to apply the accepted fixes.
                    </p>
                  </div>
                ) : pendingGate.feedbackAvailable ? (
                  <div>
                    <b>Review required</b>
                    <p className="text-foreground">
                      Messages go to the waiting agent as feedback (warm resume, no full re-run).
                      Approve / reject in the run panel.
                    </p>
                  </div>
                ) : (
                  <div>
                    <b>Run awaiting approval</b>
                    <p className="text-foreground">
                      There is no agent in the gated phase to iterate with.
                      Approve or reject in the run panel to continue.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {isSecurityReviewGate && (
            <div className="py-3">
              <SecurityReviewFindingsSection
                projectId={projectId}
                workflowRunId={pendingGate!.runId}
                canDecide={canDecideSecurityReview}
                compact
              />
            </div>
          )}

          {!pendingAlreadyInMessages && (pendingUserMessage || (pendingUserMessageAttachments && pendingUserMessageAttachments.length > 0)) && (
            <UserBubble
              text={pendingUserMessage ?? ''}
              attachments={pendingUserMessageAttachments}
              onAttachmentLoadError={onAttachmentLoadError}
            />
          )}

          {runStream.isActiveRunRunning && !runStream.pendingUserInputRequest && (
            <NodeThinkingSpinner />
          )}

          {runStream.pendingUserInputRequest && (
            <NodeQuestionPaginator
              projectId={projectId}
              pending={runStream.pendingUserInputRequest}
              onSubmitted={runStream.clearPendingUserInput}
            />
          )}

          {isStreaming && (
            <div className="py-5">
              <div className="flex gap-3">
                <AiAvatar />
                <div className="flex-1 min-w-0">
                  {isCancelling && (
                    <div role="status" aria-live="polite" data-testid="chat-cancelling-status" className="mb-2 flex items-center gap-2 text-sm text-warning-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Stopping response...</span>
                    </div>
                  )}
                  {streamingLogs && (
                    <SessionLogViewer logs={streamingLogs} isRunning={false} />
                  )}
                  {streamStartedAt && (
                    <ResponseWaitNotice
                      label={streamStatusLabel}
                      isCreatingContainer={!streamingLogs && containerStatus === 'creating_container'}
                      startedAt={streamStartedAt}
                      effort={effortApplied ? streamEffort : null}
                      effortLabel={streamEffort ? agent.effortLabel(streamEffort) : null}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {activeCommand && (
            <div className="py-4 max-w-lg mx-auto">
              <ChatCommandForm
                command={activeCommand}
                initialValues={commandPrefill}
                isExecuting={isExecutingCommand}
                onExecute={onCommandExecute}
                onCancel={onCommandCancel}
              />
            </div>
          )}

          {activeWorkflow && triggerSchema && !schemaLoading && (
            <div className="py-4 max-w-lg mx-auto">
              <WorkflowTriggerForm
                key={activeWorkflow.name}
                projectId={projectId}
                workflowName={activeWorkflow.name}
                workflowDisplayName={triggerSchema.displayName ?? activeWorkflow.displayName}
                version={activeWorkflow.version}
                description={triggerSchema.description ?? null}
                schema={triggerSchema}
                isTriggering={isTriggering}
                onTrigger={onWorkflowTrigger}
                onCancel={onWorkflowCancel}
              />
            </div>
          )}

          {activeWorkflow && schemaLoading && (
            <div className="py-4 max-w-lg mx-auto flex items-center justify-center gap-2 text-sm text-muted-foreground" role="status" aria-live="polite">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading trigger schema...
            </div>
          )}

          {activeWorkflow && !schemaLoading && schemaErrorMessage && (
            <div className="py-4 max-w-lg mx-auto">
              <div className="rounded-md border border-danger-foreground bg-danger p-3 text-sm text-danger-foreground">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  <span>{schemaErrorMessage}</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={onWorkflowRetrySchema}>
                    <RotateCcw className="mr-1 h-3.5 w-3.5" />
                    Retry
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={onWorkflowCancel}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {sendError && (
            <ChatSendErrorBanner
              message={sendError.message}
              unsentMessage={sendError.unsentMessage}
              onDismiss={onDismissSendError}
            />
          )}

          <div className="h-4" />
        </div>
      </div>

      <ChatComposer
        projectId={projectId}
        chatId={chatId}
        composerRef={composerRef}
        pendingGate={pendingGate}
        isContainerMode={isContainerMode}
        containerLockedByAttachments={containerLockedByAttachments}
        onContainerModeChange={onContainerModeChange}
        onSendMessage={onSendMessage}
        onCancel={onCancelStream}
        onCommandSelect={onCommandSelect}
        onWorkflowSelect={onWorkflowSelect}
        commands={commands}
        workflows={workflows}
        skills={skills}
        skillsLoading={skillsLoading}
        selectedSkills={selectedSkills}
        onSelectSkill={onSelectSkill}
        onRemoveSkill={onRemoveSkill}
        onClearSkills={onClearSkills}
        isStreaming={isStreaming}
        isCancelling={isCancelling}
        disabled={!chatId || !!activeCommand || !!activeWorkflow || !!runStream.pendingUserInputRequest}
        attachments={attachments}
        attachmentPreviews={attachmentPreviews}
        pendingUploadCount={pendingUploadCount}
        onAttachFiles={onAttachFiles}
        onCancelUpload={onCancelUpload}
        onRemoveAttachment={onRemoveAttachment}
        agent={agent}
      />
      </div>
    </div>
  )
}

function resolveStreamStatusLabel(containerStatus: string | null, hasLogs: boolean, isCloningRepository: boolean): string {
  if (hasLogs) return 'Processing...'
  if (!containerStatus) return 'Starting session...'
  if (containerStatus === 'creating_container') return 'Creating container environment...'
  if (containerStatus === 'reconnected') return 'Working on the response...'
  if (containerStatus === 'running') return isCloningRepository ? 'Cloning repository and starting analysis...' : 'Starting analysis...'
  return 'Processing...'
}

function AiAvatar() {
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-button text-button-foreground mt-0.5">
      <Bot className="h-4 w-4" />
    </div>
  )
}

function SystemBubble({ message }: { message: ChatMessage }) {
  const text = (message.textContent ?? '').replace(/^▶\s*/, '')
  const payload = parseSystemPayload(message.toolResultContent)
  const kind = payload?.kind

  if (kind === 'RunStarted') {
    return <RunBeacon text={text} accent="primary" />
  }
  if (kind === 'RunCompleted') {
    return <RunBeacon text={text} accent="success" />
  }
  if (kind === 'RunFailed') {
    return <RunBeacon text={text} accent="destructive" />
  }
  if (kind === 'RunCancelled') {
    return <RunBeacon text={text} accent="muted" />
  }
  const securityReviewAccent = getSecurityReviewAccent(kind)
  if (securityReviewAccent && payload) {
    return <SecurityReviewBeacon text={text} accent={securityReviewAccent} payload={payload} />
  }
  if (kind === 'PhaseDivider') {
    return <PhaseDividerBeacon text={text} />
  }

  return (
    <div className="py-3" role="alert" data-testid="system-message">
      <div className="mx-auto max-w-2xl rounded-md border border-warning-foreground bg-warning px-4 py-3">
        <div className="flex items-start gap-2">
          <TriangleAlert className="h-4 w-4 text-warning-foreground shrink-0 mt-0.5" />
          <p className="text-sm text-warning-foreground">{text}</p>
        </div>
      </div>
    </div>
  )
}

function parseSystemPayload(raw: string | null): { kind?: string; [k: string]: unknown } | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? parsed : null
  } catch {
    return null
  }
}

function RunBeacon({ text, accent }: { text: string; accent: 'primary' | 'success' | 'destructive' | 'muted' }) {
  const accentClass =
    accent === 'success' ? 'text-success-foreground'
    : accent === 'destructive' ? 'text-danger-foreground'
    : accent === 'muted' ? 'text-muted-foreground'
    : 'rounded-full px-3 py-1 bg-primary text-primary-foreground'

  return (
    <div className="py-3 flex items-center gap-3" data-testid="run-beacon">
      <div className="h-px flex-1 bg-primary" />
      <div className={`flex items-center text-xs font-semibold tracking-wide uppercase ${accentClass}`}>
        {accent === 'primary' && <CirclePlay className="h-4 w-4 mr-1" />}
        {text}
      </div>
      <div className="h-px flex-1 bg-primary" />
    </div>
  )
}

type SecurityReviewAccent = 'success' | 'destructive' | 'muted' | 'info'

const SECURITY_SEVERITY_ORDER = ['Blocker', 'Critical', 'Major', 'Minor', 'Info']

function getSecurityReviewAccent(kind: string | undefined): SecurityReviewAccent | null {
  switch (kind) {
    case 'SecurityReviewPassed':
      return 'success'
    case 'SecurityReviewFailed':
      return 'destructive'
    case 'SecurityReviewCancelled':
      return 'muted'
    case 'SecurityReviewFindingsReady':
    case 'SecurityReviewReopened':
      return 'info'
    default:
      return null
  }
}

function readCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

function readCountEntries(value: unknown, order: string[] = []): [string, number][] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return []
  const rank = (key: string) => {
    const index = order.indexOf(key)
    return index === -1 ? order.length : index
  }
  return Object.entries(value)
    .filter((entry): entry is [string, number] => (readCount(entry[1]) ?? 0) > 0)
    .sort(([a], [b]) => rank(a) - rank(b))
}

function readSafeHttpUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null
  } catch {
    return null
  }
}

function SecurityReviewBeacon({ text, accent, payload }: { text: string; accent: SecurityReviewAccent; payload: Record<string, unknown> }) {
  const accentClass =
    accent === 'success' ? 'text-success-foreground'
    : accent === 'destructive' ? 'text-danger-foreground'
    : accent === 'info' ? 'text-info-foreground'
    : 'text-muted-foreground'

  const bySeverity = readCountEntries(payload.bySeverity, SECURITY_SEVERITY_ORDER)
  const byKind = readCountEntries(payload.byKind)
  const decisionCounts: [string, number | null][] = [
    ['Accepted', readCount(payload.acceptedCount)],
    ['Rejected', readCount(payload.rejectedCount)],
    ['Applied', readCount(payload.appliedCount)],
  ]
  const decisions = decisionCounts.filter((entry): entry is [string, number] => entry[1] !== null)
  const pullRequestUrl = readSafeHttpUrl(payload.pullRequestUrl)

  const detailGroups: { label: string; entries: [string, number][] }[] = [
    { label: 'Severity', entries: bySeverity },
    { label: 'Kind', entries: byKind },
    { label: 'Decisions', entries: decisions },
  ].filter(group => group.entries.length > 0)

  return (
    <div className="py-3" data-testid="security-review-beacon">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-primary" />
        <div className={`min-w-0 max-w-[70%] text-center text-xs font-semibold tracking-wide break-words ${accentClass}`}>
          {text}
        </div>
        <div className="h-px flex-1 bg-primary" />
      </div>
      {(detailGroups.length > 0 || pullRequestUrl) && (
        <div className="mx-auto mt-2 flex max-w-2xl flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {detailGroups.length > 0 && (
            <dl className="flex flex-wrap justify-center gap-x-4 gap-y-1">
              {detailGroups.map(group => (
                <div key={group.label} className="flex gap-1">
                  <dt className="font-medium">{group.label}:</dt>
                  <dd>{group.entries.map(([name, count]) => `${name} ${count}`).join(' · ')}</dd>
                </div>
              ))}
            </dl>
          )}
          {pullRequestUrl && (
            <a
              href={pullRequestUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary underline hover:text-primary/80"
            >
              View pull request
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          )}
        </div>
      )}
    </div>
  )
}

function PhaseDividerBeacon({ text }: { text: string }) {
  return (
    <div className="py-4 flex px-10 items-center gap-3" data-testid="phase-divider">
      <div className="h-px flex-1 bg-muted-foreground" />
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{text}</div>
      <div className="h-px flex-1 bg-muted-foreground" />
    </div>
  )
}

function UserBubble({ text, timestamp, attachments, onAttachmentLoadError }: { text: string; timestamp?: string; attachments?: MessageAttachment[]; onAttachmentLoadError?: () => void }) {
  const { user } = useAuth()
  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : ''

  return (
    <div className="py-5" data-testid="user-message">
      <div className="flex gap-3 justify-end items-start">
        <div className="flex min-w-0 flex-col items-end gap-1 max-w-[80%]">
          {attachments && attachments.length > 0 && (
            <div className="flex flex-wrap justify-end gap-1.5">
              {attachments.map((a) => (
                a.url && isRenderableImage(a.contentType)
                  ? <ChatInlineImage key={a.id} attachment={a} url={a.url} onLoadError={onAttachmentLoadError} />
                  : <ChatAttachmentChip key={a.id} attachment={a} />
              ))}
            </div>
          )}
          {text && (
            <div className="rounded-2xl rounded-tr-sm bg-border px-4 py-3">
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{text}</div>
            </div>
          )}
          {timestamp && text && (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span>{formatDateTimeLong(timestamp)}</span>
              <CopyButton text={text} />
            </div>
          )}
        </div>
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold mt-0.5">
          {initials || <User className="h-3.5 w-3.5" />}
        </div>
      </div>
    </div>
  )
}

function AssistantBubble({
  content,
  timestamp,
  modelUsed,
  durationSeconds,
  toolCallsMade,
  toolCallsTracked,
}: {
  content: string
  timestamp?: string
  modelUsed?: string | null
  durationSeconds?: number | null
  toolCallsMade?: string | null
  toolCallsTracked?: boolean
}) {
  const hasStructuredLogs = useMemo(() => {
    if (!content) return false
    const events = parseSessionLogs(content)
    return events.some((e) => e.type !== 'plain')
  }, [content])

  const hasMetadata = modelUsed || durationSeconds != null || toolCallsMade || toolCallsTracked

  const markdownRef = useRef<HTMLDivElement>(null)
  const getMarkdownHtml = useCallback(() => markdownRef.current?.innerHTML, [])

  return (
    <div className="py-5">
      <div className="flex gap-3">
        <AiAvatar />
        <div className="min-w-0 max-w-[70%]">
          {hasStructuredLogs ? (
            <SessionLogViewer logs={content} isRunning={false} />
          ) : (
            <div ref={markdownRef} className="prose prose-sm max-w-none text-foreground">
              <MarkdownViewer content={content} bodyTextClass="text-foreground" />
            </div>
          )}
          {hasMetadata && (
            <div className="mt-2 inline-flex bg-background flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border px-2 py-1 text-sm text-muted-foreground">
              {modelUsed && (
                <span className="inline-flex items-center gap-1">
                  <BotMessageSquare className="h-3 w-3 text-foreground" />
                  <span className="font-medium text-foreground">{modelUsed}</span>
                </span>
              )}
              {durationSeconds != null && (
                <span className="inline-flex items-center gap-1 text-foreground">
                  <Clock className="h-3 w-3 text-foreground" />
                  {durationSeconds < 60 ? `${durationSeconds}s` : `${Math.floor(durationSeconds / 60)}m ${durationSeconds % 60}s`}
                </span>
              )}
              {toolCallsMade ? (
                <span className="inline-flex items-center gap-1 text-foreground">
                  <Wrench className="h-3 w-3 text-foreground" />
                  {toolCallsMade}
                </span>
              ) : toolCallsTracked ? (
                <span className="inline-flex items-center gap-1" data-testid="no-tool-calls-indicator">
                  Answered without project data
                </span>
              ) : null}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
            {timestamp && <span>{formatDateTimeLong(timestamp)}</span>}
            <CopyButton
              text={content}
              getHtml={hasStructuredLogs ? undefined : getMarkdownHtml}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

type ConversationTurn =
  | { type: 'user'; message: ChatMessage }
  | { type: 'assistant'; message: ChatMessage }
  | { type: 'system'; message: ChatMessage }
  | { type: 'connector-expired-notification'; message: ChatMessage; payload: ConnectorExpiredNotificationPayload }
  | { type: 'connector-missing-notification'; message: ChatMessage; payload: ConnectorMissingNotificationPayload }
  | { type: 'context-budget-exhausted-notification'; message: ChatMessage; payload: ContextBudgetExhaustedNotificationPayload }
  | { type: 'command-result'; message: ChatMessage; commandName: string; commandDisplayName: string; params: Record<string, string>; result: { success: boolean; outputs: Record<string, string>; errorMessage: string | null; durationMs: number } }
  | { type: 'workflow-run'; message: ChatMessage; runId: string }
  | { type: 'answered-question'; message: ChatMessage; payload: { requestId: string; questions: string; answers: string; notes: string | null } }

function groupIntoTurns(messages: ChatMessage[]): ConversationTurn[] {
  const turns: ConversationTurn[] = []

  for (const msg of messages) {
    if (msg.role === 'User') {
      if (msg.toolResultContent) {
        try {
          const parsed = JSON.parse(msg.toolResultContent) as {
            kind?: string
            requestId?: string
            questions?: string
            answers?: string
            notes?: string | null
          }
          if (parsed.kind === 'AnsweredQuestion' && parsed.requestId) {
            turns.push({
              type: 'answered-question',
              message: msg,
              payload: {
                requestId: parsed.requestId,
                questions: parsed.questions ?? '[]',
                answers: parsed.answers ?? '[]',
                notes: parsed.notes ?? null,
              },
            })
            continue
          }
        } catch {
          // not JSON
        }
      }
      turns.push({ type: 'user', message: msg })
    } else if (msg.role === 'Assistant') {
      turns.push({ type: 'assistant', message: msg })
    } else if (msg.role === 'System') {
      turns.push({ type: 'system', message: msg })
    } else if (msg.role === 'Notification' && msg.toolResultContent) {
      try {
        const payload = JSON.parse(msg.toolResultContent) as {
          type?: string
          connectorType?: string
          repositoryFullName?: string | null
          message?: string
          currentSpendUsd?: number | null
          capUsd?: number | null
        }
        if (payload.type === ConnectorExpiredNotificationType && payload.connectorType && payload.message) {
          turns.push({
            type: 'connector-expired-notification',
            message: msg,
            payload: {
              type: ConnectorExpiredNotificationType,
              connectorType: payload.connectorType,
              repositoryFullName: payload.repositoryFullName ?? null,
              message: payload.message,
            },
          })
        } else if (payload.type === ConnectorMissingNotificationType && payload.connectorType && payload.message) {
          turns.push({
            type: 'connector-missing-notification',
            message: msg,
            payload: {
              type: ConnectorMissingNotificationType,
              connectorType: payload.connectorType,
              repositoryFullName: payload.repositoryFullName ?? null,
              message: payload.message,
            },
          })
        } else if (payload.type === ContextBudgetExhaustedNotificationType && payload.message) {
          turns.push({
            type: 'context-budget-exhausted-notification',
            message: msg,
            payload: {
              type: ContextBudgetExhaustedNotificationType,
              message: payload.message,
              currentSpendUsd: payload.currentSpendUsd ?? null,
              capUsd: payload.capUsd ?? null,
            },
          })
        }
      } catch {
        // ignore non-structured notification payloads
      }
    } else if (msg.role === 'ToolResult' && msg.toolResultContent) {
      try {
        const payload = JSON.parse(msg.toolResultContent) as {
          type?: string
          command?: { name?: string; displayName?: string; parameters?: Record<string, string> }
          result?: { success?: boolean; outputs?: Record<string, string>; errorMessage?: string | null; durationMs?: number }
          workflow?: { runId?: string }
        }

        const commandName = payload.command?.name
        const commandDisplayName = payload.command?.displayName ?? commandName
        const commandParams = payload.command?.parameters ?? {}
        const normalizedResult = payload.result
          ? {
              success: payload.result.success ?? false,
              outputs: payload.result.outputs ?? {},
              errorMessage: payload.result.errorMessage ?? null,
              durationMs: payload.result.durationMs ?? 0,
            }
          : null

        if (payload.type === 'chat-command-result' && commandName && normalizedResult) {
          turns.push({
            type: 'command-result',
            message: msg,
            commandName,
            commandDisplayName: commandDisplayName ?? commandName,
            params: commandParams,
            result: normalizedResult,
          })
        }

        if (payload.type === 'workflow-run' && payload.workflow?.runId) {
          turns.push({
            type: 'workflow-run',
            message: msg,
            runId: payload.workflow.runId,
          })
        }
      } catch {
        // ignore non-structured tool results
      }
    }
  }

  return turns
}
